import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { CompanySignUpDTO } from "@/api/data-contracts";
import { useParams } from "react-router-dom";
import { AppFrame } from "@/app/AppFrame";
import { useModelState } from "@/lib/modelstate";
import { useSignUpSchema } from "@/pages/company-sign-up-page/schema";
import { languageOptions, useAvailableLanguages } from "@/datacaches";
import { authenticationApi } from "@/boxhub-api";
import { AxiosError } from "axios";
import { CompanySignUpForm } from "@/pages/company-sign-up-page/CompanySignUpForm";
import { Step, StepLabel, Stepper } from "@mui/material";
import { FinishedPage, FinishedPageProps } from "@/common/FinishedPage";
import { useTranslation } from "react-i18next";
import { AuthI18N, CompanyI18N, UserI18N } from "@/translations";
import { encodePasswordBase64 } from "@/lib/utils";
import { changeAppLanguage } from "@/app/locale/i18nBundle";
import { withLanguageAwareChange } from "@/pages/company-sign-up-page/languageAwareChange";

export const CompanySignUpPage = () => {
    const { t } = useTranslation();
    const params = useParams();
    const [finishedInfo, setFinishedInfo] = React.useState<FinishedPageProps | null>(null);
    const [availableLanguages] = useAvailableLanguages();
    // While `useAvailableLanguages()` is loading (or permanently, on a fetch error) it resolves
    // to `[]` — fall back to the static baseline so the sign-up dropdown isn't empty and the
    // schema's `.oneOf(...)` never validates against an empty set.
    const effectiveLanguages = availableLanguages.length > 0 ? availableLanguages : languageOptions;
    const schema = useSignUpSchema(effectiveLanguages);
    const pageTitle = t(AuthI18N.login.page.signUpTitle);

    const onLoadError = (error: AxiosError<unknown>) => {
        const data = error.response?.data as { error?: { message?: string } } | undefined;
        setFinishedInfo({
            success: false,
            title: t(AuthI18N.login.message.registrationFailed),
            subTitle: data?.error?.message ?? t(AuthI18N.login.message.registrationFailedDescription)
        });
        return true;
    };

    const [activeStep, setActiveStep] = useState(0);

    // Define all field names per step (including the user data step)
    const allStepFields = useMemo(
        () =>
            [
                [
                    "email",
                    "password",
                    "confirmPassword",
                    "salutation",
                    "firstName",
                    "lastName",
                    "phone",
                    "fax",
                    "language",
                    "ceo"
                ],
                [
                    "companyName",
                    "country",
                    "legalForm",
                    "streetAddress",
                    "houseNumber",
                    "postalCode",
                    "city",
                    "website",
                    "commercialRegisterNumber",
                    "vatId"
                ],
                [
                    "erpManufacturer",
                    "erpSoftwareName",
                    "erpSoftwareVersion",
                    "miniliner",
                    "midliner",
                    "maxiliner",
                    "dieCutting",
                    "folderGluerMachine",
                    "packaging",
                    "hazardousGoodsQualification",
                    "flexoPrinting",
                    "hdFlexoPrinting",
                    "offsetPrinting",
                    "digitalPrinting",
                    "officeHours",
                    "additionalInformation"
                ],
                ["bankName", "iban", "bic", "paymentTerms"],
                ["privacyPolicyAccepted"],
                ["contractAndUsageAccepted"],
                ["confidentialityAccepted"]
            ] as (keyof CompanySignUpDTO)[][],
        []
    );

    const { model, errors, onChange, status, save, validate } = useModelState<CompanySignUpDTO, string>({
        initialState: {
            miniliner: false,
            midliner: false,
            maxiliner: false,
            dieCutting: false,
            folderGluerMachine: false,
            packaging: false,
            hazardousGoodsQualification: false,
            flexoPrinting: false,
            hdFlexoPrinting: false,
            offsetPrinting: false,
            digitalPrinting: false
        } as CompanySignUpDTO,
        schema,
        id: params.token,
        loader: authenticationApi.beginCompanySignUp,
        updater: authenticationApi.performCompanySignUp,
        context: () => ({
            schemaFields: stepFields[activeStep] ?? null
        }),
        sendTransformer: (model) => ({
            ...model,
            password: model.password ? encodePasswordBase64(model.password) : model.password,
            confirmPassword: model.confirmPassword
                ? encodePasswordBase64(model.confirmPassword)
                : model.confirmPassword,
            // Commercial register number is optional
            // Setting undefined if empty to avoid Validation Error in DTO in Backend
            commercialRegisterNumber: model.commercialRegisterNumber ? model.commercialRegisterNumber : undefined
        }),
        onLoadError
    });

    // Wrap the model-state change handler so a manual language-dropdown change also switches the
    // live app language. The switch is transient (no persistence); the model still stores the value
    // for submit. `changeAppLanguage` returns a Promise but swallows its own errors, so passing it
    // as a `void`-returning callback is safe — its result is intentionally not awaited here.
    const languageAwareChange = useMemo(() => withLanguageAwareChange(onChange, changeAppLanguage), [onChange]);

    // When the user is already confirmed, skip the user data step (step 0)
    const allSteps = useMemo(
        () => [
            t(UserI18N.form.page.title),
            t(CompanyI18N.form.page.title),
            t(CompanyI18N.form.page.moreDataTitle),
            t(CompanyI18N.form.page.bankConnectionTitle),
            t(AuthI18N.login.field.privacyPolicy),
            t(AuthI18N.login.field.termsAndConditions),
            t(AuthI18N.login.field.confidentialityAgreement)
        ],
        [t]
    );

    const steps = useMemo(
        () => (model?.userAlreadyRegistered ? allSteps.slice(1) : allSteps),
        [allSteps, model?.userAlreadyRegistered]
    );

    const stepFields = useMemo(
        () => (model?.userAlreadyRegistered ? allStepFields.slice(1) : allStepFields),
        [allStepFields, model?.userAlreadyRegistered]
    );

    const handleSubmit = () => {
        save()?.then(() => {
            setFinishedInfo({
                success: true,
                title: t(AuthI18N.login.message.registrationSuccess),
                subTitle: t(AuthI18N.login.message.registrationUnderReview)
            });
        });
    };

    const buttons = useMemo(
        () => (
            <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel sx={{ whiteSpace: "nowrap" }}>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
        ),
        [activeStep, steps]
    );

    useEffect(() => {
        validate({ silent: true });
    }, [activeStep, validate]);

    if (finishedInfo) {
        return <FinishedPage {...finishedInfo} />;
    }

    return (
        <AppFrame loading={status === "loading" || status === "saving"} title={pageTitle} buttons={buttons} buttonsGrow>
            <CompanySignUpForm
                model={model}
                onChange={languageAwareChange}
                errors={errors}
                onSubmit={handleSubmit}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                steps={steps}
            />
        </AppFrame>
    );
};
